import { HotkeyModule } from "angular2-hotkeys";
import { UIRouterModule } from "@uirouter/angular";
import { CdkDrag, CdkDropList } from "@angular/cdk/drag-drop";
import { PortalModule } from "@angular/cdk/portal";
import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NzOutletModule } from "ng-zorro-antd/core/outlet";
import { NzPipesModule } from "ng-zorro-antd/pipes";
import { AccountsModule } from "@webapp/accounts/accounts.module";
import { AccountServiceProvider } from "@webapp/accounts/providers/account.provider";
import { AssigneesModule } from "@webapp/assignees/assignees.module";
import { CommentsModule } from "@webapp/comments/comments.module";
import { ThreadComponent } from "@webapp/comments/components/thread/thread.component";
import { ProgressColoringProvider } from "@webapp/configuration/providers/progress-coloring.provider";
import { CustomFieldsModule } from "@webapp/custom-fields/custom-fields.module";
import { AutoFieldsApiService } from "@webapp/data-story/services/auto-fields-facade/auto-fields-api.service";
import { AutoFieldsFacade } from "@webapp/data-story/services/auto-fields-facade/auto-fields-facade.service";
import { AutoFieldsState } from "@webapp/data-story/services/auto-fields-facade/auto-fields-state.service";
import { AutoInsightsApiService } from "@webapp/data-story/services/auto-insights-facade/auto-insights-api.service";
import { AutoInsightsFacade } from "@webapp/data-story/services/auto-insights-facade/auto-insights-facade.service";
import { AutoInsightsState } from "@webapp/data-story/services/auto-insights-facade/auto-insights-state.service";
import { FeatureTogglesModule } from "@webapp/feature-toggles/feature-toggles.module";
import { LinksModule } from "@webapp/links/links.module";
import { LocalizationModule } from "@webapp/localization/localization.module";
import { GoalProgressBarComponent } from "@webapp/okrs/components/goal-bar-progress/goal-bar-progress.component";
import { OkrMetricBarBooleanComponent } from "@webapp/okrs/components/metric-bar/metric-bar-boolean/okr-metric-bar-boolean.component";
import { OkrMetricBarProgressComponent } from "@webapp/okrs/components/metric-bar/metric-bar-progress/okr-metric-bar-progress.component";
import { OkrMetricBarTargetComponent } from "@webapp/okrs/components/metric-bar/metric-bar-target/okr-metric-bar-target.component";
import { OkrContributorItemComponent } from "@webapp/okrs/components/okr-contributors/okr-contributor-item/okr-contributor-item.component";
import { OkrContributorsComponent } from "@webapp/okrs/components/okr-contributors/okr-contributors.component";
import { OkrTaskRowComponent } from "@webapp/okrs/components/okr-tasks/components/okr-task-row/okr-task-row.component";
import { OkrTasksComponent } from "@webapp/okrs/components/okr-tasks/okr-tasks.component";
import { OkrWorkflowAvatarGroupComponent } from "@webapp/okrs/components/okr-workflow/okr-workflow-avatar-group/okr-workflow-avatar-group.component";
import { OkrWorkflowComponent } from "@webapp/okrs/components/okr-workflow/okr-workflow.component";
import { OkrAttainmentPickerComponent } from "@webapp/okrs/goals/components/attainment-picker/attainment-picker.component";
import { OkrMethodologyFacadeProvider } from "@webapp/okrs/goals/providers/okr-methodology.facade.provider";
import { ConfidenceLevelSelectorComponent } from "@webapp/okrs/metrics/components/confidence-level-selector/confidence-level-selector.component";
import { ConfidencePreviewComponent } from "@webapp/okrs/metrics/components/confidence-preview/confidence-preview.component";
import { LimitationsReachedComponent } from "@webapp/okrs/metrics/components/create-metric-form/components/limitations-reached/limitations-reached.component";
import { NoPermissionsComponent } from "@webapp/okrs/metrics/components/create-metric-form/components/no-permissions/no-permissions.component";
import { CreateMetricFormComponent } from "@webapp/okrs/metrics/components/create-metric-form/create-metric-form.component";
import { MetricDeadlineSelectorComponent } from "@webapp/okrs/metrics/components/metric-deadline-selector/metric-deadline-selector.component";
import { MetricProgressBarComponent } from "@webapp/okrs/metrics/components/metric-progress-bar/metric-progress-bar.component";
import { MetricSnapshotPreviewComponent } from "@webapp/okrs/metrics/components/metric-snapshots/metric-snapshot-preview/metric-snapshot-preview.component";
import { MetricSnapshotsComponent } from "@webapp/okrs/metrics/components/metric-snapshots/metric-snapshots.component";
import { MetricTypeSelectorComponent } from "@webapp/okrs/metrics/components/metric-type-selector/metric-type-selector.component";
import { PiSuggestionSharedGoalStateServiceProvider, PiSuggestionSharedMetricStateServiceProvider } from "@webapp/okrs/metrics/providers/metrics.provider";
import { ConfidenceSettingsService } from "@webapp/okrs/metrics/services/confidence-settings.service";
import { OkrViewsModule } from "@webapp/okrs/okr-views/okr-views.module";
import { CurrentUserServiceProvider } from "@webapp/people/providers/people.provider";
import { PIExpandableSuggestionsModule } from "@webapp/platform-intelligence/expandable-suggestions/expandable-suggestions.module";
import { PISuggestionDrawerModule } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/suggestion-drawer.module";
import { PISuggestionStateIndicatorModule } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-state-indicator/suggestion-state-indicator.module";
import { QuantivePlusModule } from "@webapp/platform-intelligence/quantive-plus/quantive-plus.module";
import { PiContextualSuggestionButtonModule } from "@webapp/platform-intelligence/shared/components/pi-contextual-suggestion-button/pi-contextual-suggestion-button.module";
import { PlanningSessionsServiceProvider } from "@webapp/sessions/providers/sessions.provider";
import { SessionsModule } from "@webapp/sessions/sessions.module";
import { ButtonSelectModule } from "@webapp/shared/components/button-select/button-select.module";
import { ButtonComponent } from "@webapp/shared/components/button/button.component";
import { ConfidenceLabelComponent } from "@webapp/shared/components/confidence-label/confidence-label.component";
import { GifViewModule } from "@webapp/shared/components/gif-view/gif-view.module";
import { HelpTooltipComponent } from "@webapp/shared/components/help-tooltip/help-tooltip.component";
import { ItemAutomationModule } from "@webapp/shared/components/item-automation/item-automation.module";
import { MultiSelectorModule } from "@webapp/shared/components/multi-selector/multi-selector.module";
import { PeopleSelectorModule } from "@webapp/shared/components/people-selector/people-selector.module";
import { ProgressIndicatorLabelModule } from "@webapp/shared/components/progress-indicator-label/progress-indicator-label.module";
import { DateViewModule } from "@webapp/shared/date-view/date-view.module";
import { CdnImgModule } from "@webapp/shared/directives/cdn-img/cdn-img.module";
import { DropdownModule } from "@webapp/shared/dropdown/dropdown.module";
import { IconWithPopoverModule } from "@webapp/shared/icon-with-popover/icon-with-popover.module";
import { IndicatorModule } from "@webapp/shared/indicator/indicator.module";
import { CommonPipeModule } from "@webapp/shared/pipes/common/common-pipe.module";
import { DayjsDateModule } from "@webapp/shared/pipes/dayjs-date/dayjs-date.module";
import { PolicyServiceProvider } from "@webapp/shared/providers/policy.service";
import { RichTextEditorWithMentionsModule } from "@webapp/shared/rich-text-editor-with-mentions/rich-text-editor-with-mentions.module";
import { RichTextEditorModule } from "@webapp/shared/rich-text-editor/rich-text-editor.module";
import { UnifiedFiltersModule } from "@webapp/shared/unified-filters/unified-filters.module";
import { UnitSelectorModule } from "@webapp/shared/unit-selector/unit-selector.module";
import { TasksModule } from "@webapp/tasks/tasks.module";
import { UiAccessibilityModule } from "@webapp/ui/accessibility/accessibility.module";
import { UiAlertModule } from "@webapp/ui/alert/alert.module";
import { UiAssigneeModule } from "@webapp/ui/assignee/assignee.module";
import { UiAvatarModule } from "@webapp/ui/avatar/avatar.module";
import { UiButtonModule } from "@webapp/ui/button/button.module";
import { UiDatePickerModule } from "@webapp/ui/date-picker/date-picker.module";
import { UiDrawerModule } from "@webapp/ui/drawer/drawer.module";
import { UiDropdownModule } from "@webapp/ui/dropdown/dropdown.module";
import { UiFormModule } from "@webapp/ui/form/form.module";
import { UiGridModule } from "@webapp/ui/grid/grid.module";
import { UiIconModule } from "@webapp/ui/icon/icon.module";
import { UiInputNumberModule } from "@webapp/ui/input-number/input-number.module";
import { UiInputModule } from "@webapp/ui/input/input.module";
import { UiLabelModule } from "@webapp/ui/label/label.module";
import { UiListModule } from "@webapp/ui/list/list.module";
import { UiLoadingIndicatorModule } from "@webapp/ui/loading-indicator/loading-indicator.module";
import { UiMenuModule } from "@webapp/ui/menu/menu.module";
import { UiModalModule } from "@webapp/ui/modal/modal.module";
import { UiNotificationModule } from "@webapp/ui/notification/notification.module";
import { UiPipesModule } from "@webapp/ui/pipes/pipes.module";
import { UiPopoverModule } from "@webapp/ui/popover/popover.module";
import { UiProgressModule } from "@webapp/ui/progress/progress.module";
import { UiRadioModule } from "@webapp/ui/radio/radio.module";
import { UiSelectModule } from "@webapp/ui/select/select.module";
import { UiSkeletonModule } from "@webapp/ui/skeleton/skeleton.module";
import { UiSliderModule } from "@webapp/ui/slider/slider.module";
import { UiTagModule } from "@webapp/ui/tag/tag.module";
import { UiToggleModule } from "@webapp/ui/toggle/toggle.module";
import { UiTooltipModule } from "@webapp/ui/tooltip/tooltip.module";
import { UiTreeSelectModule } from "@webapp/ui/tree-select/tree-select.module";
import { UiTypographyModule } from "@webapp/ui/typography/typography.module";
import { ConfirmGoalDeleteComponent } from "./components/confirm-goal-delete/confirm-goal-delete.component";
import { GoalWorkflowStatusLabelComponent } from "./components/goal-workflow-status-label/goal-workflow-status-label.component";
import { TaskStatusModule } from "./components/okr-tasks/components/task-status/task-status.module";
import { ParentSelectorModule } from "./components/parent-selector/parent-selector.module";
import { SectionTitleWithIconComponent } from "./components/section-title-with-icon/section-title-with-icon.component";
import { GoalPrivacyButtonComponent } from "./goals/components/goal-privacy-button/goal-privacy-button.component";
import { GoalsBulkDeleteComponent } from "./goals/components/goals-bulk-delete/goals-bulk-delete.component";
import { OkrDesignScoreComponent } from "./goals/components/okr-design-score/okr-design-score.component";
import { GoalsApiService } from "./goals/services/goals-api.service";
import { GoalsFacade } from "./goals/services/goals-facade.service";
import { MetricContributorDropdownComponent } from "./metrics/components/metric-contributor-dropdown/metric-contributor-dropdown.component";
import { MetricMilestoneComponent } from "./metrics/components/metric-milestone/metric-milestone.component";
import { MetricNumberInputFieldComponent } from "./metrics/components/metric-number-input-field/metric-number-input-field.component";
import { UpdateMetricNoteSkeletonComponent } from "./metrics/components/update-metric-note/update-metric-note-skeleton/update-metric-note-skeleton.component";
import { UpdateMetricNoteComponent } from "./metrics/components/update-metric-note/update-metric-note.component";
import { UpdateMetricSkeletonComponent } from "./metrics/components/update-metric/update-metric-skeleton/update-metric-skeleton.component";
import { UpdateMetricComponent } from "./metrics/components/update-metric/update-metric.component";
import { OkrsCoreModule } from "./okrs-core.module";
import { OkrsRoutingModule } from "./okrs-routing.module";
import { ConfirmGoalDeleteService } from "./services/confirm-goal-delete.service";

@NgModule({
  declarations: [
    GoalsBulkDeleteComponent,
    OkrDesignScoreComponent,
    SectionTitleWithIconComponent,
    GoalPrivacyButtonComponent,
    MetricNumberInputFieldComponent,
    MetricProgressBarComponent,
    MetricContributorDropdownComponent,
    GoalWorkflowStatusLabelComponent,
    MetricDeadlineSelectorComponent,
    ConfidenceLevelSelectorComponent,
    MetricTypeSelectorComponent,
    OkrTasksComponent,
    OkrTaskRowComponent,
    OkrWorkflowAvatarGroupComponent,
    OkrWorkflowComponent,
    UpdateMetricSkeletonComponent,
    CreateMetricFormComponent,
    NoPermissionsComponent,
    LimitationsReachedComponent,
    UpdateMetricSkeletonComponent,
    MetricSnapshotsComponent,
    MetricSnapshotPreviewComponent,
    ConfidencePreviewComponent,
    OkrMetricBarTargetComponent,
    OkrMetricBarBooleanComponent,
    OkrMetricBarProgressComponent,
    UpdateMetricNoteComponent,
    UpdateMetricNoteSkeletonComponent,
    MetricMilestoneComponent,
    OkrContributorsComponent,
    OkrContributorItemComponent,
    ConfidenceLabelComponent,
    UpdateMetricComponent,
    UpdateMetricSkeletonComponent,
    GoalProgressBarComponent,
    ConfirmGoalDeleteComponent,
    OkrAttainmentPickerComponent,
    ThreadComponent,
  ],
  imports: [
    PiContextualSuggestionButtonModule,
    FeatureTogglesModule,
    QuantivePlusModule,
    PIExpandableSuggestionsModule,
    OkrsRoutingModule,
    OkrsCoreModule,
    SessionsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AssigneesModule,
    LocalizationModule,
    IndicatorModule,
    UiAlertModule,
    UiIconModule,
    UiProgressModule,
    UiPopoverModule,
    UiButtonModule,
    UiSelectModule,
    UiAssigneeModule,
    UiTreeSelectModule,
    UiFormModule,
    UiGridModule,
    UiModalModule,
    UiInputModule,
    UiInputNumberModule,
    UiNotificationModule,
    UiTypographyModule,
    UiLabelModule,
    ParentSelectorModule,
    DropdownModule,
    LinksModule,
    UiTooltipModule,
    TasksModule,
    UiDatePickerModule,
    UiTagModule,
    UiSliderModule,
    UiLoadingIndicatorModule,
    CdnImgModule,
    ButtonSelectModule,
    MultiSelectorModule,
    CommentsModule,
    UiPipesModule,
    IconWithPopoverModule,
    OkrViewsModule,
    UIRouterModule,
    UiAvatarModule,
    RichTextEditorWithMentionsModule,
    UiRadioModule,
    CustomFieldsModule,
    UiListModule,
    UiSkeletonModule,
    DateViewModule,
    GifViewModule,
    DayjsDateModule,
    CommonPipeModule,
    UnifiedFiltersModule,
    UnitSelectorModule,
    UiToggleModule,
    ItemAutomationModule,
    RichTextEditorModule,
    PeopleSelectorModule,
    CustomFieldsModule,
    NzOutletModule,
    NzPipesModule,
    PortalModule,
    HelpTooltipComponent,
    CdkDropList,
    CdkDrag,
    HotkeyModule,
    UiAccessibilityModule,
    UiDropdownModule,
    UiMenuModule,
    ButtonComponent,
    PISuggestionDrawerModule,
    PISuggestionStateIndicatorModule,
    AccountsModule,
    TaskStatusModule,
    ProgressIndicatorLabelModule,
    UiDrawerModule,
  ],
  exports: [
    OkrDesignScoreComponent,
    SectionTitleWithIconComponent,
    GoalPrivacyButtonComponent,
    MetricNumberInputFieldComponent,
    MetricProgressBarComponent,
    MetricContributorDropdownComponent,
    MetricDeadlineSelectorComponent,
    ConfidenceLevelSelectorComponent,
    MetricTypeSelectorComponent,
    UpdateMetricComponent,
    UpdateMetricNoteComponent,
    OkrTasksComponent,
    MetricMilestoneComponent,
    OkrContributorsComponent,
    OkrAttainmentPickerComponent,
    MetricSnapshotsComponent,
    MetricSnapshotPreviewComponent,
    ConfidencePreviewComponent,
  ],
  providers: [
    PiSuggestionSharedGoalStateServiceProvider,
    PiSuggestionSharedMetricStateServiceProvider,
    PlanningSessionsServiceProvider,
    PolicyServiceProvider,
    AccountServiceProvider,
    ConfidenceSettingsService,
    CurrentUserServiceProvider,
    ProgressColoringProvider,
    OkrMethodologyFacadeProvider,
    ProgressColoringProvider,
    ConfirmGoalDeleteService,
    AutoFieldsFacade,
    AutoFieldsState,
    AutoFieldsApiService,
    AutoInsightsFacade,
    AutoInsightsState,
    AutoInsightsApiService,
    GoalsApiService,
    GoalsFacade,
  ],
})
export class OkrsModule {}
